"use client";

import { useCart } from "@/hooks/useCart";
import { usePrismicProducts, useProducts } from "@/hooks/useProduct";
import { useClearCart } from "@/hooks/useCartMutations";
import { useCheckoutStore } from "@/stores/checkout-store";
import { Locales } from "@/lib/i18n";
import { StoreCartLineItem, StoreProduct } from "@medusajs/types";
import { ProductDocument } from "@/prismicio-types";
import { useMemo, useRef, useEffect, useState } from "react";
import { getCartTotalQuantity } from "@/lib/cart";

export const ITEMS_PER_PAGE = 10;

export const useCartProducts = (locale: Locales) => {
  const { cart } = useCart();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const setData = useCheckoutStore((state) => state.setData);

  const { clearCart, isLoading: isClearingCart } = useClearCart({
    onSuccess: () => {
      setIsModalOpen(false);
    },
    onError: (err) => {
      console.error("Error clearing cart:", err);
      setIsModalOpen(false);
    },
  });

  const handleClearCart = () => {
    setData({ promoCode: "" }); // Clear promo code when cart is cleared
    clearCart();
  };

  const prevCartItemsRef = useRef(cart?.items);
  const cartLineCount = cart?.items?.length || 0;
  const itemCount = getCartTotalQuantity(cart?.items);

  // Stable handles - only add new handles, don't remove
  const allMedusaIdsRef = useRef<Set<string>>(new Set());
  const [stableHandlesArray, setStableHandlesArray] = useState<string[]>([]);

  // Track product IDs and keep them stable while the cart refetches.
  useEffect(() => {
    const currentMedusaIds =
      cart?.items?.map((item) => item.product_id).filter((productId) => productId !== undefined) ?? [];
    let hasNewHandles = false;

    // Add new handles to stable set
    currentMedusaIds.forEach((id) => {
      if (id && !allMedusaIdsRef.current.has(id)) {
        allMedusaIdsRef.current.add(id);
        hasNewHandles = true;
      }
    });

    // Update the array state only if we have new handles (this will trigger refetch)
    if (hasNewHandles) {
      setStableHandlesArray(Array.from(allMedusaIdsRef.current));
    }
  }, [cart?.items]);

  // Reset to first page if current page exceeds total pages
  useEffect(() => {
    const totalPages = Math.ceil(cartLineCount / ITEMS_PER_PAGE);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [cartLineCount, currentPage]);

  // Update the ref after render
  useEffect(() => {
    prevCartItemsRef.current = cart?.items;
  });

  // Fetch products using stable handles (only triggers when new handles are added)
  const { data: productsData = [], isLoading: isMedusaProductsLoading } = useProducts(locale, stableHandlesArray);
  const { data: prismicProducts = [], isLoading: isPrismicProductsLoading } = usePrismicProducts(
    locale,
    stableHandlesArray,
    {
      fetch: [
        "product.title",
        "product.images",
        "product.featuredImage",
        "product.image_full_size_card",
        "product.medusaId",
        "product.description",
      ],
    }
  );

  const titleCollator = useMemo(() => new Intl.Collator(locale, { numeric: true, sensitivity: "base" }), [locale]);

  // Create mapping for CartProducts with stable alphabetical ordering.
  const cartItemsWithProducts = useMemo(() => {
    if (!cart?.items || !productsData) return [];

    // Handle the case where productsData might be an array or object
    const productsMap = Array.isArray(productsData) ? {} : productsData;

    return cart.items
      .map((cartItem) => {
        const medusaId = cartItem?.product_id;
        if (!medusaId) return null;

        const medusaProduct = productsMap?.[medusaId];
        const prismicProduct = prismicProducts?.find((product) => product.data?.medusaId === medusaProduct?.id);
        return medusaProduct
          ? {
              cartItem,
              product: medusaProduct,
              prismicProduct,
              sortTitle: prismicProduct?.data?.title || medusaProduct.title || "",
            }
          : null;
      })
      .filter(Boolean)
      .sort((a, b) => {
        const titleComparison = titleCollator.compare(a?.sortTitle || "", b?.sortTitle || "");

        if (titleComparison !== 0) {
          return titleComparison;
        }

        return (a?.cartItem.id || "").localeCompare(b?.cartItem.id || "");
      }) as {
      cartItem: StoreCartLineItem;
      product: StoreProduct;
      prismicProduct: ProductDocument;
      sortTitle: string;
    }[];
  }, [cart?.items, productsData, prismicProducts, titleCollator]);

  // Loading states
  const isInitialLoad =
    (isMedusaProductsLoading || isPrismicProductsLoading) &&
    (cart === undefined || (cart?.items && cart.items.length > 0));

  const isOptimisticUpdate = useMemo(() => {
    if (!prevCartItemsRef.current || !cart?.items) return false;

    const prevCount = prevCartItemsRef.current.length;
    const currentCount = cart.items.length;

    // Check if we have products data available
    const hasProductsData = Object.keys(productsData).length > 0;

    return prevCount > currentCount && hasProductsData;
  }, [cart?.items, productsData]);

  const shouldShowLoader = isInitialLoad && !isOptimisticUpdate;

  // Pagination calculations
  const totalItems = cartItemsWithProducts.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalItems);
  const currentPageItems = cartItemsWithProducts.slice(startIndex, endIndex);

  // Pagination handlers
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return {
    // Cart data
    cart,
    itemCount,

    // Modal state
    isModalOpen,
    setIsModalOpen,

    // Pagination
    currentPage,
    setCurrentPage,

    // Clear cart
    handleClearCart,
    isClearingCart,

    // CartProducts & Pagination
    cartItemsWithProducts,
    shouldShowLoader,
    totalItems,
    totalPages,
    startIndex,
    endIndex,
    currentPageItems,
    goToNextPage,
    goToPrevPage,
    goToPage,
  };
};
